/**
 * Inter School Quiz season rules — live collection holds Feb 2026+ only
 * after confirmation; last-year teams live in archived_quiz_registrations_2025.
 */

export const QUIZ_COLLECTION = "registrations_inter_school_quiz";
export const QUIZ_ARCHIVE_COLLECTION = "archived_quiz_registrations_2025";

/** First day of BTUI 2026 quiz season (local midnight). */
export const QUIZ_SEASON_CUTOFF = new Date(2026, 1, 1);

const MAX_QUIZ_TEAMS = 2;

export const QUIZ_CONFIRM_TYPES = {
  SAME_RETURNING: "same_returning",
  NEW_TEAMS: "new_teams",
  NOT_RETURNING: "not_returning",
};

export const QUIZ_CONFIRM_LABELS = {
  [QUIZ_CONFIRM_TYPES.SAME_RETURNING]: "Same team(s) returning",
  [QUIZ_CONFIRM_TYPES.NEW_TEAMS]: "New/different teams",
  [QUIZ_CONFIRM_TYPES.NOT_RETURNING]: "Old teams removed (not coming)",
};

/** Short label for remove-old-teams quick action. */
export function removeOldTeamsLabel(staleCount) {
  const n = Math.max(0, staleCount ?? 0);
  if (n === 0) return "Remove old teams";
  if (n === 1) return "Remove old team (not coming)";
  if (n === 2) return "Remove both old teams";
  return `Remove ${n} old teams`;
}

export function schoolKeyFromRecord(record) {
  return (
    (record.schoolKey ?? (record.schoolName ?? "").trim().toLowerCase()) ||
    "(unknown)"
  );
}

export function isCurrentSeasonQuizRecord(record) {
  if (record.collection !== QUIZ_COLLECTION) return false;
  const at = record.at;
  if (!at || !(at instanceof Date) || Number.isNaN(at.getTime())) return false;
  return at >= QUIZ_SEASON_CUTOFF;
}

export function isStaleQuizRecord(record) {
  if (record.collection !== QUIZ_COLLECTION) return false;
  const at = record.at;
  if (!at || !(at instanceof Date) || Number.isNaN(at.getTime())) return true;
  return at < QUIZ_SEASON_CUTOFF;
}

/**
 * @returns {{ current: Record<string, number>, stale: Record<string, number> }}
 */
export function summarizeQuizBySchool(records) {
  const current = {};
  const stale = {};
  for (const r of records) {
    if (r.collection !== QUIZ_COLLECTION) continue;
    const k = schoolKeyFromRecord(r);
    if (isCurrentSeasonQuizRecord(r)) {
      current[k] = (current[k] ?? 0) + 1;
    } else {
      stale[k] = (stale[k] ?? 0) + 1;
    }
  }
  return { current, stale };
}

export function resolveQuizConfirmType(schoolEntry) {
  if (!schoolEntry) return null;
  if (schoolEntry.quizConfirmType) return schoolEntry.quizConfirmType;
  if (schoolEntry.quizConfirmedAt) {
    const n = Number(schoolEntry.quizReturningTeams);
    if (Number.isFinite(n) && n > 0) return QUIZ_CONFIRM_TYPES.SAME_RETURNING;
    return QUIZ_CONFIRM_TYPES.NOT_RETURNING;
  }
  return null;
}

export function isQuizConfirmed(schoolEntry) {
  return resolveQuizConfirmType(schoolEntry) != null;
}

export function maxReturningTeams(currentCount, staleCount) {
  const cur = currentCount ?? 0;
  const stale = staleCount ?? 0;
  return Math.min(stale, Math.max(0, MAX_QUIZ_TEAMS - cur));
}

/**
 * Effective quiz team count for X/2 — live 2026 docs + confirmed same-team carry-over.
 */
export function getEffectiveQuizTeams(
  currentCount,
  quizReturningTeams,
  quizConfirmType
) {
  const cur = Math.max(0, currentCount ?? 0);
  const type = quizConfirmType ?? null;

  if (type === QUIZ_CONFIRM_TYPES.SAME_RETURNING) {
    const returning = Math.max(
      0,
      Math.min(Number(quizReturningTeams) || 0, MAX_QUIZ_TEAMS - cur)
    );
    return Math.min(MAX_QUIZ_TEAMS, cur + returning);
  }

  return Math.min(MAX_QUIZ_TEAMS, cur);
}

export function needsQuizConfirmation(staleCount, schoolEntry) {
  return (staleCount ?? 0) > 0 && !isQuizConfirmed(schoolEntry);
}

export function enrichRowQuizMeta(rowKey, schoolState, quizCurrentBySchool, quizStaleBySchool) {
  const current = quizCurrentBySchool[rowKey] ?? 0;
  const stale = quizStaleBySchool[rowKey] ?? 0;
  const entry = schoolState?.[rowKey] ?? {};
  const quizConfirmType = resolveQuizConfirmType(entry);
  const rawReturning = entry.quizReturningTeams;
  const quizReturningTeams =
    rawReturning === undefined || rawReturning === null
      ? null
      : Number(rawReturning);
  const effective = getEffectiveQuizTeams(
    current,
    quizReturningTeams,
    quizConfirmType
  );
  const pending = needsQuizConfirmation(stale, entry);

  return {
    quizCurrentTeams: current,
    quizStaleTeams: stale,
    quizReturningTeams,
    quizConfirmType,
    quizTeams: effective,
    quizPending: pending,
  };
}
